/**
 * Página pública de cancelamento de agendamento
 * 
 * Permite que o convidado cancele seu agendamento usando o cancel_token
 * recebido no email de confirmação.
 * 
 * Rota: /schedule/cancel/:token
 * Sem autenticação necessária.
 * 
 * RELEASE: 2026-02-14
 * VERSION: 1.0.0
 */

import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useCancelBooking } from '@/hooks/usePublicScheduling';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { CalendarX2, CheckCircle2, AlertTriangle, Loader2 } from 'lucide-react';

/** Resultado retornado pela RPC após cancelamento */
interface CancelResult {
  booking_id: string;
  event_title: string;
  start_at: string;
  guest_name: string;
  google_event_id: string | null;
  user_id: string;
}

const PublicSchedulingCancel = () => {
  // tenantSlug presente na rota para consistência, cancelamento usa apenas o token
  const { token } = useParams<{ tenantSlug: string; token: string }>();
  const cancelMutation = useCancelBooking();

  // Estado local do formulário
  const [reason, setReason] = useState('');
  const [cancelResult, setCancelResult] = useState<CancelResult | null>(null);

  /** Confirma o cancelamento */
  const handleCancel = async () => {
    if (!token) return;

    try {
      const result = await cancelMutation.mutateAsync({
        cancelToken: token,
        reason: reason.trim() || undefined,
      });
      setCancelResult(result as CancelResult);
    } catch {
      // Erro tratado pelo mutation state
    }
  };

  // Formatar data para exibição (quando cancelado com sucesso)
  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', {
      weekday: 'long',
      day: '2-digit',
      month: 'long',
      year: 'numeric',
    });
  };

  const formatTime = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Token inválido (rota sem token)
  if (!token) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md text-center">
          <CardContent className="pt-8 pb-8 space-y-4">
            <AlertTriangle className="h-12 w-12 text-destructive mx-auto" />
            <p className="text-muted-foreground">Link de cancelamento inválido.</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  // Tela de sucesso após cancelamento
  if (cancelResult) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8 space-y-6 text-center">
            <div className="flex h-16 w-16 items-center justify-center rounded-full bg-primary/10 mx-auto">
              <CheckCircle2 className="h-8 w-8 text-primary" />
            </div>
            <div className="space-y-2">
              <h2 className="text-xl font-bold text-foreground">
                Agendamento Cancelado
              </h2>
              <p className="text-muted-foreground text-sm">
                Seu agendamento foi cancelado com sucesso.
              </p>
            </div>
            <div className="bg-muted/50 rounded-lg p-4 text-left space-y-1">
              <p className="text-sm font-medium text-foreground">
                {cancelResult.event_title}
              </p>
              <p className="text-sm text-muted-foreground">
                📆 {formatDate(cancelResult.start_at)}
              </p>
              <p className="text-sm text-muted-foreground">
                🕐 {formatTime(cancelResult.start_at)}
              </p>
            </div>
          </CardContent>
        </Card>
        {/* Footer */}
        <p className="fixed bottom-4 left-0 right-0 text-center text-xs text-muted-foreground">
          Powered by UaizyChat
        </p>
      </div>
    );
  }

  // Formulário de cancelamento
  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="flex h-14 w-14 items-center justify-center rounded-full bg-destructive/10 mx-auto mb-2">
            <CalendarX2 className="h-7 w-7 text-destructive" />
          </div>
          <CardTitle className="text-xl">Cancelar Agendamento</CardTitle>
          <CardDescription>
            Tem certeza que deseja cancelar seu agendamento? Esta ação não pode ser desfeita.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Campo opcional para motivo */}
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground">
              Motivo do cancelamento (opcional)
            </label>
            <Textarea
              placeholder="Conte-nos o motivo do cancelamento..."
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              rows={3}
              maxLength={500}
            />
          </div>

          {/* Mensagem de erro */}
          {cancelMutation.isError && (
            <div className="flex items-start gap-2 bg-destructive/10 text-destructive rounded-lg p-3 text-sm">
              <AlertTriangle className="h-4 w-4 mt-0.5 shrink-0" />
              <span>
                {(cancelMutation.error as Error)?.message?.includes('já foi cancelado')
                  ? 'Este agendamento já foi cancelado anteriormente.'
                  : (cancelMutation.error as Error)?.message?.includes('inválido')
                    ? 'Link de cancelamento inválido ou expirado.'
                    : 'Ocorreu um erro ao cancelar. Tente novamente.'}
              </span>
            </div>
          )}

          {/* Botão de cancelamento */}
          <Button
            variant="destructive"
            className="w-full"
            onClick={handleCancel}
            disabled={cancelMutation.isPending}
          >
            {cancelMutation.isPending ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Cancelando...
              </>
            ) : (
              'Confirmar Cancelamento'
            )}
          </Button>
        </CardContent>
      </Card>
      {/* Footer */}
      <p className="fixed bottom-4 left-0 right-0 text-center text-xs text-muted-foreground">
        Powered by UaizyChat
      </p>
    </div>
  );
};

export default PublicSchedulingCancel;
